 

 import { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github.css";
import "highlight.js/styles/github-dark.css";

import { FileText, Book, Calendar, Search } from "lucide-react";

function Document() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Load file list
  useEffect(() => {
    fetch("/Documents/Backend/files.json")
      .then((res) => res.json())
      .then((data) => {
        setFileList(data);
        if (data.length > 0) setSelectedFile(data[0]);
      })
      .catch((err) => console.error("Error loading file list:", err));
  }, []);

  // Load markdown content
  useEffect(() => {
    if (!selectedFile) return;
    setLoading(true);

    fetch(`/Documents/Backend/${selectedFile}`)
      .then((res) => res.text())
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading markdown:", err);
        setLoading(false);
      });
  }, [selectedFile]);

  // Search filter
  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim()) return fileList;
    return fileList.filter((file) =>
      file.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [fileList, searchTerm]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* === MAIN CONTENT === */}
 <main className="flex-1 overflow-y-auto bg-gray-50">
  <div classNcame="max-w-5xl mx-auto py-16 px-10">
    {loading ? (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-b-2 border-blue-600 rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading documentation...</p>
        </div>
      </div>
    ) : (
      <article
         className="bg-white p-14 shadow-lg rounded-lg border border-gray-200
  prose prose-lg max-w-none leading-relaxed
  prose-headings:font-bold prose-headings:tracking-tight
  prose-p:text-gray-800 prose-p:leading-relaxed prose-p:my-4 prose-p:text-justify
  prose-a:text-blue-600 hover:prose-a:text-blue-800 prose-a:font-medium
  prose-code:bg-gray-900 prose-code:text-white prose-code:px-2 prose-code:py-1 prose-code:rounded-md prose-code:font-mono prose-code:text-sm
  prose-pre:bg-gray-950 prose-pre:text-gray-100 prose-pre:p-5 prose-pre:rounded-xl prose-pre:shadow-xl prose-pre:border prose-pre:border-gray-800 prose-pre:my-6
  prose-pre:overflow-x-auto prose-pre:scrollbar-thin prose-pre:scrollbar-thumb-gray-700 prose-pre:scrollbar-track-gray-900
  prose-ul:my-6 prose-li:text-gray-800 prose-li:leading-relaxed prose-li:marker:text-blue-600
  prose-ol:my-6 prose-li:my-1
  prose-strong:text-gray-900 prose-strong:font-semibold
  prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:px-5 prose-blockquote:py-3 prose-blockquote:italic prose-blockquote:rounded-r prose-blockquote:text-gray-700 prose-blockquote:my-6
  prose-table:w-full prose-table:border-collapse prose-th:bg-gray-100 prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:border prose-th:border-gray-300 prose-td:p-3 prose-td:border prose-td:border-gray-300 prose-tr:even:bg-gray-50 prose-tr:hover:bg-blue-50
  prose-hr:my-10 prose-hr:border-gray-300
  prose-a:text-blue-600 hover:prose-a:text-blue-800 prose-a:underline-offset-2 hover:prose-a:underline prose-a:font-semibold"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
          components={{
            h1: ({ node, ...props }) => {
              const text = String(props.children).replace(/\s+/g, "-").toLowerCase();
              return (
                <h1
                  id={text}
                  className="text-center text-4xl font-extrabold text-gray-900 mt-2 mb-12 border-b-2 border-gray-200 pb-4 tracking-tight scroll-mt-28"
                  {...props}
                />
              );
            },
            h2: ({ node, ...props }) => {
              const text = String(props.children).replace(/\s+/g, "-").toLowerCase();
              return (
                <h2
                  id={text}
                  className="text-2xl font-bold text-gray-800 mt-12 mb-4 pb-1 border-b border-gray-200 scroll-mt-24"
                  {...props}
                />
              );
            },
            h3: ({ node, ...props }) => {
              const text = String(props.children).replace(/\s+/g, "-").toLowerCase();
              return (
                <h3
                  id={text}
                  className="text-xl font-semibold text-gray-700 mt-8 mb-3 scroll-mt-20"
                  {...props}
                />
              );
            },
            table: ({ node, ...props }) => (
              <div className="overflow-x-auto my-8">
                <table
                  className="min-w-full border border-gray-300 rounded-lg overflow-hidden shadow-sm"
                  {...props}
                />
              </div>
            ),
            th: ({ node, ...props }) => (
              <th
                className="bg-blue-50 text-gray-900 font-semibold border border-gray-300 p-3 text-left"
                {...props}
              />
            ),
            td: ({ node, ...props }) => (
              <td
                className="border border-gray-300 p-3 text-gray-700"
                {...props}
              />
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </article>
    )}
  </div>
</main>




      {/* === RIGHT SIDEBAR === */}
      <aside className="w-72 bg-white border-l border-gray-200 shadow-lg flex flex-col">
        {/* HEADER */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-regalBlue -600 to-regalBlue -700">
          <div className="flex items-center gap-3 mb-2">
            <Book className="w-6 h-6 text-white" />
            <h1 className="text-xl font-bold text-white">Documentation</h1>
          </div>
          <p className="text-sm text-blue-100">Wreckonnect App</p>
        </div>
 

        {/* FILE LIST */}
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {filteredFiles.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-3">
              No documents found
            </p>
          ) : (
            filteredFiles.map((file) => (
              <div
                key={file}
                onClick={() => setSelectedFile(file)}
                className={`group flex items-center gap-3 p-3 text-sm rounded-lg cursor-pointer transition-all duration-200 ${
                  file === selectedFile
                    ? "bg-regalBlue -600 text-white shadow-md scale-[1.02]"
                    : "hover:bg-gray-100 text-gray-700 hover:shadow-sm"
                }`}
              >
                <FileText
                  className={`w-4 h-4 flex-shrink-0 ${
                    file === selectedFile ? "text-white" : "text-blue-600"
                  }`}
                />
                <span className="truncate font-medium">{file.replace(".md", "")}</span>
              </div>
            ))
          )}
        </div>

        {/* FOOTER */}
        <div className="p-4 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-3 h-3" />
            <span>Updated: {new Date().toLocaleDateString()}</span>
          </div>
          <div className="font-medium text-gray-700">
            {fileList.length} documents available
          </div>
        </div>
      </aside>
    </div>
  );
}

export default Document;
